// apps/web/src/auth/userCacheReset.ts
import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.js';
import { REQUESTS_FILTERS_STORAGE_KEY } from '../features/requests/useRequestsFilters.js';
import { CARGO_FILTERS_STORAGE_KEY } from '../features/cargo/cargoFiltersStorage.js';
import { __resetAllPlayedSoundCursors } from '../features/notifications/sound/playedCursor.js';

/**
 * @aiPurpose subscribe to identity transitions in `useAuthStore` and, on every
 *   real identity change (`A→B`, `A→null`, `null→A`), wipe the entire React
 *   Query cache. Prevents the in-process cache from leaking row-level or
 *   permission-gated data across same-tab user switches: if Manager A's rows
 *   are still within `staleTime` when Manager B logs in, an unguarded cache
 *   read would serve A's visibility scope to B before any refetch could
 *   re-apply the server's row filter.
 *
 *   D-1 (#1660, 2026-06-12): full `queryClient.clear()` (queries + mutations)
 *   replaces the formerly-enumerated key prefix list. Coverage is now
 *   guaranteed structurally — any user-scoped query added to any feature
 *   module is cleared automatically. The React Query cache is in-memory only
 *   (no persistence plugin), so `clear()` has no durable side-effects.
 *
 *   Issue #630 also removes the persisted `/requests` filter localStorage key
 *   on real identity boundary transitions (logout, user switch, cross-tab
 *   session-loss). Bootstrap (`null → user`) does NOT remove the key — that
 *   is the F5 path where the page hydrated a shared URL into localStorage
 *   before the refresh cookie restored the session, and wiping it here would
 *   break the feature on every refresh.
 * @aiInvariant every identity transition calls `queryClient.clear()`, which
 *   removes ALL query and mutation cache entries. Coverage is structural, not
 *   list-based — no per-prefix maintenance is required. Non-identity store
 *   updates (token rotation, bootstrap-flag toggle) must NOT trigger a clear;
 *   the `lastUserId` guard enforces this.
 * @aiSecurity authentication boundary — the cache clear is the only barrier
 *   against cross-user data exposure between the in-process React Query cache
 *   and a freshly-installed session. The localStorage filter cleanup is
 *   best-effort: a tab that crashes before logout cleanup runs leaves the
 *   manager-UUID workflow context in storage on a shared workstation (accepted
 *   residual risk; backend visibility remains authoritative).
 */
export function subscribeUserCacheReset(qc: QueryClient): () => void {
  let lastUserId: string | null = useAuthStore.getState().user?.id ?? null;
  return useAuthStore.subscribe((state) => {
    const currentId = state.user?.id ?? null;
    if (currentId === lastUserId) return;
    const previousId = lastUserId;
    lastUserId = currentId;
    qc.clear();
    __resetAllPlayedSoundCursors();
    // WHY (#630, #2104): localStorage cleanup (both the /requests and /cargo
    //   persisted filter keys) runs ONLY when there was a previous
    //   authenticated user. The bootstrap transition `null -> user` is
    //   intentionally skipped because shared URLs may have written filter
    //   payloads before refresh-cookie bootstrap completed; wiping the
    //   keys here would erase them on every F5.
    if (previousId !== null) {
      try {
        localStorage.removeItem(REQUESTS_FILTERS_STORAGE_KEY);
        localStorage.removeItem(CARGO_FILTERS_STORAGE_KEY);
      } catch {
        // best-effort — degraded private modes / storage exceptions do not
        // crash the auth pipeline.
      }
    }
  });
}
